import Image from 'next/image';
import Link from 'next/link';
import {UnifiedProductDisplay, isGroupProduct, EventSchedule} from '@/app/api/types';
import { ChatBubbleLeftIcon, ClockIcon, PhoneIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { VideoCameraIcon } from '@heroicons/react/24/outline';
import { Award } from "flowbite-react-icons/outline";
import { formatInTimeZone } from 'date-fns-tz';
import {generateGuideSlug} from "@/app/lib/slugUtil";
import {stripHtmlTags} from "@/app/lib/utils";
import { formatPricePerMinute } from "@/app/lib/utils";
import { GuideBadge } from '@/components/guide/GuideBadge';
import { GuideBadgeKey } from '@/app/config/badges';

interface UnifiedProductCardProps {
  product: UnifiedProductDisplay;
  index?: number;
}

/**
 * UnifiedProductCard component that handles both individual and group products
 * with a consistent layout while highlighting their unique features
 */
export function UnifiedProductCard({ product, index }: UnifiedProductCardProps) {
  const isGroup = isGroupProduct(product);
  const isCritical = index === 0;
  console.log(`[UnifiedProductCard] Rendering product ID: ${product.id}, Name: ${product.name}, isGroup: ${isGroup}`);
  if (isGroup) {
    console.log(`[UnifiedProductCard] Schedules received for ${product.id}:`, product.schedules);
  }

  // Format functions
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const formatEventDate = (isoString: string) => {
    return formatInTimeZone(
      new Date(isoString),
      'Europe/Berlin',
      "dd.MM.yyyy 'um' HH:mm 'Uhr'"
    );
  };

  // For group products, get future schedules
  const futureSchedules = isGroup
    ? product.schedules?.filter(schedule => new Date(schedule.startTime) > new Date())
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()) ?? []
    : [];

  const nextSchedule = futureSchedules[0];
  const additionalSchedules = futureSchedules.length - 1;
  const badgesToDisplay = product.guide.badges ?? [];
  return (
    <div className="bg-white border border-gray-200 rounded-lg group flex flex-col h-full min-h-[550px] min-w-[200px]">
      {/* Image Section */}
      <div className="relative w-full h-48">
        <Image
          src={product.teaserImage || '/images/lp/cards/placeholder-image.jpg'}
          alt={product.name || 'Produkt Vorschaubild'}
          fill
          style={{ objectFit: 'cover' }}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          className="rounded-t-lg"
          loading={isCritical ? 'eager' : 'lazy'}
          priority={isCritical}
        />

        {product.featured && (
          <div
            className="absolute top-2 right-2 bg-primary-500 text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-1">
            <Award className="w-4 h-4"/>
            <span>Featured</span>
          </div>
        )}

        {/* Guide Profile Picture */}
        <div className="absolute -bottom-8 left-4">
          <Link
            href={`/guide/${generateGuideSlug(`${product.guide.givenName} ${product.guide.familyName}`, product.guide.id)}`}>
            <div className="w-16 h-16 rounded-full border-4 border-white overflow-hidden relative">
              <Image
                src={product.guide.profileImage || '/images/guide/placeholder.webp'}
                alt={`Profilbild ${product.guide.givenName} ${product.guide.familyName}`.trim() || 'Guide Profilbild'}
                fill
                sizes="64px"
                className="object-cover"
              />
            </div>
          </Link>
        </div>
      </div>

      {/* Content Section */}
      <div className="pt-8 px-4 pb-4 flex flex-col flex-grow">
        {/* Guide Name */}
        <div className="mb-0">
          <Link
            href={`/guide/${generateGuideSlug(`${product.guide.givenName} ${product.guide.familyName}`, product.guide.id)}`}
            className="text-sm font-bold text-gray-600 hover:text-gray-900 transition-colors"
          >
            {`${product.guide.givenName} ${product.guide.familyName}`.trim()}
          </Link>
        </div>

        {/* Product Details */}
        <div className="flex-grow">
          <h5 className="mb-2 text-lg md:text-xl font-semibold tracking-tight text-gray-900 line-clamp-2"
              style={{lineHeight: "1.0"}}>
            {product.name}
          </h5>

          {/* Price for individual products */}
          {!isGroup && (
            <div className="mb-3">
              <span className="text-lg font-bold">{formatPrice(product.price)}€</span>
              <span className="text-[10px] text-gray-400 pl-2"> inkl. MwSt.</span>
              {product.duration > 0 && (
                <div className="text-sm text-gray-500 italic mt-1">
                  entspricht {formatPricePerMinute(product.price, product.duration)} € pro Minute
                </div>
              )}
            </div>
          )}

          {/* Badges Container - Below prices, full width */}
          {badgesToDisplay.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3 w-full overflow-x-auto pb-1">
              {badgesToDisplay.map((badgeKey) => (
                <GuideBadge key={badgeKey} badgeKey={badgeKey as GuideBadgeKey} />
              ))}
            </div>
          )}

          {/* Description */}
          <p className="mb-3 text-sm md:text-sm text-gray-700 line-clamp-4">
            {stripHtmlTags(product.description)}
          </p>

          {/* Product Type & Duration */}
          <div className="flex items-center gap-4 mb-3">
            {!isGroup && (
              <div className="flex items-center text-gray-600">
                <ClockIcon className="w-5 h-5 mr-1"/>
                <span className="text-sm">{product.duration} Minuten</span>
              </div>
            )}
            {isGroup ? (
              <div className="flex items-center text-gray-600">
                <VideoCameraIcon className="w-5 h-5 mr-1"/>
                <span className="text-sm">Video-Gruppenevent</span>
              </div>
            ) : (
              <div className="flex items-center text-gray-600">
                {product.type === 'CHAT' ? (
                  <>
                    <ChatBubbleLeftIcon className="w-5 h-5 mr-1"/>
                    <span className="text-sm">Chat</span>
                  </>
                ) : product.type === 'CALL' ? (
                  <>
                    <PhoneIcon className="w-5 h-5 mr-1"/>
                    <span className="text-sm">Anruf</span>
                  </>
                ) : (
                  <>
                    <VideoCameraIcon className="w-5 h-5 mr-1"/>
                    <span className="text-sm">Video</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Categories */}
          {/*{product.categories && (*/}
          {/*  <div className="flex flex-wrap items-center mt-2 -m-1">*/}
          {/*    {product.categories.map(category => (*/}
          {/*      <Link*/}
          {/*        href={`/category/${category.id}`}*/}
          {/*        key={category.id}*/}
          {/*        className="bg-white border-gray-200 border-2 text-primary-200 text-xs font-medium m-1 px-2.5 py-0.5 rounded-full hover:text-primary-dark hover:border-primary-dark transition-colors duration-200"*/}
          {/*      >*/}
          {/*        {category.name}*/}
          {/*      </Link>*/}
          {/*    ))}*/}
          {/*  </div>*/}
          {/*)}*/}

          {/* Schedule Section for Group Products */}
          {isGroup && futureSchedules.length > 0 && (
            <div className="mt-4 space-y-2">
              <h6 className="font-medium text-gray-900">Nächster Termin:</h6>
              {nextSchedule ? (
                <>
                  <div
                    className="bg-white border border-primary-200 p-3 rounded-lg hover:border-primary-300 transition-colors">
                    <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">
                      {formatEventDate(nextSchedule.startTime)}
                    </span>
                      <span className="text-sm text-gray-600">
                      {nextSchedule.pricePerSeat} €
                    </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <UserGroupIcon className="w-4 h-4 mr-1"/>
                      <span>
                        {nextSchedule.currentParticipantCount ?? 0}/{nextSchedule.maxParticipants} Teilnehmer
                      </span>
                    </div>
                  </div>
                  {additionalSchedules > 0 && (
                    <p className="text-sm text-gray-500 italic">
                      +{additionalSchedules} weitere Termine
                    </p>
                  )}
                </>
              ) : null}
            </div>
          )}
        </div>

        {/* Book Button */}
        <Link
          href={isGroup ? `/group-event/${product.id}` : `/product/${product.id}`}
          className={`block`}
        >
          <button className="
            mt-4
            w-full
            bg-primary
            hover:bg-primary-dark
            text-white
            px-2
            pt-1
            h-10
            font-bold
            rounded-full
            cursor-pointer
            transition-colors
            duration-200
            flex items-center justify-center
          ">
            MEHR ERFAHREN
          </button>
        </Link>
      </div>
    </div>
  );
}